import { usePage } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';

interface AiContentSectionProps {
    name: string;
    className?: string;
}

interface SectionData {
    type: 'text' | 'image';
    content: string | null;
    imageUrl: string | null;
}

export function AiContentSection({ name, className = '' }: AiContentSectionProps) {
    const { sections } = usePage().props as unknown as { sections: Record<string, SectionData> };

    const section = sections?.[name];

    if (!section) {
        return <div className={className}>No content available</div>;
    }

    if (section.type === 'text' && section.content) {
        // Special formatting for specific sections
        if (name === 'iconic-quote') {
            return (
                <blockquote className={`text-2xl font-serif italic leading-relaxed ${className}`}>
                    "{section.content}"
                </blockquote>
            );
        }
        
        if (name === 'hook-tagline') {
            return (
                <h3 className={`text-xl font-bold leading-tight ${className}`}>
                    {section.content}
                </h3>
            );
        }

        // For all other sections, use ReactMarkdown to parse and render
        return (
            <div className={className}>
                <ReactMarkdown 
                    components={{
                        // Custom component overrides for consistent styling
                        h1: ({children}) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                        h2: ({children}) => <h2 className="text-xl font-semibold mb-3">{children}</h2>,
                        h3: ({children}) => <h3 className="text-lg font-medium mb-2">{children}</h3>,
                        p: ({children}) => <p className="leading-relaxed mb-4 last:mb-0">{children}</p>,
                        ul: ({children}) => <ul className="list-disc list-inside space-y-2 mb-4">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal list-inside space-y-2 mb-4">{children}</ol>,
                        li: ({children}) => {
                            // Handle both tight and loose lists properly
                            // Loose lists (with inline formatting) wrap content in <p> tags
                            // We need to handle this without breaking the layout
                            return (
                                <li className="leading-relaxed [&>p]:inline [&>p]:m-0 [&>p:last-child]:m-0">
                                    {children}
                                </li>
                            );
                        },
                        blockquote: ({children}) => (
                            <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-4">
                                {children}
                            </blockquote>
                        ),
                        code: ({inline, children}) => 
                            inline ? (
                                <code className="px-1 py-0.5 bg-muted rounded text-sm">{children}</code>
                            ) : (
                                <code className="block p-4 bg-muted rounded overflow-x-auto text-sm">{children}</code>
                            ),
                        strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                        em: ({children}) => <em className="italic">{children}</em>,
                        hr: () => <hr className="my-6 border-border" />,
                    }}
                >
                    {section.content}
                </ReactMarkdown>
            </div>
        );
    }

    if (section.type === 'image' && section.imageUrl) {
        return <img src={section.imageUrl} alt={name} className={className} />;
    }

    return <div className={className}>No content available</div>;
}
